import React from "react";
import { Grid, TextField, Box, Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NumericFormat } from "react-number-format";
import TruMidRfqFields from "./TruMidRFQFields";
import MarketAxessFields from "./MarketAxessFields";
import StrategyParamFields from "./StrategyParamFields";
import { PROTOCOL_TYPES } from "../../Constants/AppConstants";
import { inputStyle, accordionStyle, accordionSummaryStyle, accordionDetailsStyle } from "./styles";
import RejectReason from "./ActualOrderTabRejectReason";

const OrderProtocolSection = ({
  actualOrderData,
  errors,
  isFieldEditable,
  handleChange,
  clearFieldError,
  clearModalError,
  setActualOrderData,
  readOnly,
  authToken,
  fieldOptions,
  filterVenues,
  handleVenueChange,
  settlementDate,
  formatSettlementDate,
  paramLoading,
  paramError,
  handleStrategyParamChange,
  editRfqMode,
}) => {
  return (
    <>
      {/* TruMid RFQ specific fields */}
      {actualOrderData.protocol === PROTOCOL_TYPES.TRUMID_RFQ && (
        <TruMidRfqFields
          actualOrderData={actualOrderData}
          handleChange={handleChange}
          errors={errors}
          isFieldEditable={isFieldEditable}
          clearFieldError={clearFieldError}
          clearModalError={clearModalError}
          setActualOrderData={setActualOrderData}
          readOnly={readOnly}
        />
      )}

      {/* MarketAxess specific fields */}
      {actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS && (
        <MarketAxessFields
          actualOrderData={actualOrderData}
          handleChange={handleChange}
          errors={errors}
          isFieldEditable={isFieldEditable}
          clearFieldError={clearFieldError}
          clearModalError={clearModalError}
          setActualOrderData={setActualOrderData}
          venueOptions={filterVenues(fieldOptions.venue)}
          handleVenueChange={handleVenueChange}
          authToken={authToken}
          readOnly={readOnly}
        />
      )}

      {/* Generic RFQ TTL */}
      {actualOrderData.protocol === PROTOCOL_TYPES.RFQ && (
        <Grid item xs={3}>
          <NumericFormat
            customInput={TextField}
            name="ttl"
            value={actualOrderData.ttl ?? ""}
            variant="filled"
            fullWidth
            label="TTL (seconds)"
            placeholder="Enter TTL (60-3000 seconds)"
            thousandSeparator=","
            allowNegative={false}
            decimalScale={0}
            disabled={!isFieldEditable("ttl")}
            error={errors?.ttl}
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
            isAllowed={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) return true;
              return floatValue <= 3000;
            }}
            onValueChange={(values) => {
              const { floatValue } = values;
              if (floatValue === undefined) {
                handleChange({ target: { name: "ttl", value: "" } });
                return;
              }
              handleChange({ target: { name: "ttl", value: floatValue } });
            }}
          />
        </Grid>
      )}

      {/* Settlement Date */}
      {settlementDate && (
        <Grid item xs={3}>
          <TextField
            fullWidth
            variant="filled"
            label="Settlement Date"
            value={formatSettlementDate(settlementDate)}
            disabled
            sx={inputStyle}
            InputProps={{ disableUnderline: true }}
          />
        </Grid>
      )}
      
        {/* Reject Reason - Only shown in readonly mode if present */}
        {readOnly && (
          <RejectReason rejectReason={actualOrderData.rejectReason} />
        )}
      {/* MarketAxess Strategy Parameters */}
      {actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS && (
        <Grid item xs={12} sx={{ mt: readOnly && actualOrderData.rejectReason ? -2.5 : 0 }}>
          <Accordion sx={accordionStyle} defaultExpanded={!readOnly}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyle}>
              <Typography variant="subtitle1" fontWeight={600}>
                Strategy Parameters
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={accordionDetailsStyle}>
              <Grid container spacing={2}>
                {paramLoading && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading strategy parameters…</Typography>
                    </Box>
                  </Grid>
                )}
                {paramError && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="error">
                      {paramError}
                    </Typography>
                  </Grid>
                )}
                {!paramLoading && !paramError && (
                  <StrategyParamFields
                    params={actualOrderData.strategyParamDefinitions || []}
                    values={actualOrderData.strategyParameters || {}}
                    errors={errors?.strategyParameters || {}}
                    onChange={handleStrategyParamChange}
                    isFieldEditable={isFieldEditable}
                    editRfqMode={editRfqMode}
                  />
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </>
  );
};

export default OrderProtocolSection;
